import moment from "moment";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { Modal, Button, Form } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { ACTIVITY_TYPES, ACTIVITY_RESULT_TYPES } from "../strings";
import { showError } from "../../services/toast-service";
import { ContactLaterDialogSchema } from "../../services/validation-schema";
import { useTranslation } from "react-i18next";
import BusinessTimeInfo from "./business-time-info";
import {
  formatLocal,
  localToInstant,
  pickerDateToLocal
} from "../../services/business-time";

const PickerDialog = ({
  show,
  onHide,
  activityHandler,
  minimumDateError,
  minimumHoursFromNow = null,
  hint = ""
}) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [minimumDate, setMinimumDate] = useState(() =>
    moment().add(minimumHoursFromNow || 0, "hours")
  );
  const errorBorderClasses = "border border-2 border-danger";
  const initialValues = { date: "" };
  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(ContactLaterDialogSchema)
  });
  const {
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors }
  } = methods;
  const selectedDate = watch("date");
  const submitDate = async (values) => {
    // What the agent typed is German time, whatever timezone the PC runs
    // on: it is sent as wall clock and never converted with the PC's zone.
    const localDateTime = pickerDateToLocal(values.date);
    let contactLaterOn;
    try {
      contactLaterOn = localToInstant(localDateTime);
    } catch (error) {
      setError("date", {
        type: "businessTime",
        message: t("leadAgent.appointmentTimeInvalid")
      });
      return;
    }
    if (minimumDate && moment(contactLaterOn).isBefore(minimumDate)) {
      setError("date", {
        type: "min",
        message: minimumDateError || t("leadAgent.contactLaterPastDateError")
      });
      return;
    }
    try {
      if (isUpdating) return;
      // What the agent typed is German time, whatever timezone the PC runs
      // on: it is sent as wall clock and never converted with the PC's zone.
      const localDateTime = pickerDateToLocal(values.date);
      try {
        localToInstant(localDateTime);
      } catch (error) {
        setError("date", {
          type: "businessTime",
          message: t("leadAgent.appointmentTimeInvalid")
        });
        return;
      }
      setIsUpdating(true);
      await activityHandler({
        type: ACTIVITY_TYPES.CALL,
        result: ACTIVITY_RESULT_TYPES.CONTACT_LATER,
        comment: `${t("common.on")} ${formatLocal(localDateTime)}`,
        contactLaterLocal: localDateTime,
        // same wall clock for a backend without contactLaterLocal
        contactLaterVal: formatLocal(localDateTime, "YYYY-MM-DD HH:mm:00")
      });
      handleCloseDialog();
    } catch (error) {
      console.log("Error in creating activity and updating lead", error);
      showError(error.response?.data?.message);
    } finally {
      setIsUpdating(false);
    }
  };
  const handleCloseDialog = () => {
    reset(initialValues);
    onHide();
  };

  useEffect(() => {
    if (!show) return;
    const nextMinimumDate = moment().add(minimumHoursFromNow || 0, "hours");
    setMinimumDate(nextMinimumDate);
    if (minimumHoursFromNow) {
      reset({ date: nextMinimumDate.toDate() });
    }
  }, [minimumHoursFromNow, reset, show]);

  return (
    <Modal
      show={show}
      centered
      size="md"
      backdrop="static"
      className="text-center"
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <Modal.Header className="justify-content-center">
        <h5 className="mb-0">{t("leadAgent.contactLaterDialogHeading")}</h5>
      </Modal.Header>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(submitDate)}>
          <div className="text-center my-4">
            <ReactDatePicker
              selected={selectedDate}
              minDate={minimumDate.toDate()}
              dateFormat="MMMM d, yyyy h:mm aa"
              onChange={(selectedDate) => {
                setValue("date", selectedDate);
                clearErrors("date");
              }}
              calendarClassName={errors?.date ? errorBorderClasses : ""}
              showTimeInput // Enables time selection
              timeInputLabel="Time:"
              inline // to show picker without display input
            />
            {errors?.date && (
              <div className="text-danger fw-bold">
                {errors?.date?.message}*
              </div>
            )}
            <BusinessTimeInfo
              localDateTime={
                selectedDate ? pickerDateToLocal(selectedDate) : ""
              }
            />
            {hint && <div className="text-secondary small mt-2">{hint}</div>}
          </div>
          <Modal.Footer>
            <Button
              disabled={isUpdating}
              onClick={handleCloseDialog}
              variant="outline-secondary"
            >
              {t("common.close")}
            </Button>
            <Button disabled={isUpdating} type="submit" variant="success">
              {t("common.save")}
            </Button>
          </Modal.Footer>
        </Form>
      </FormProvider>
    </Modal>
  );
};
export default PickerDialog;
